import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Collapse, Divider, Drawer,
    FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
    Tab, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Layout from './Layout';
import InventoryService from '../services/InventoryService';
import { useToast } from '../contexts/ToastContext';
import styles from './css/Inventory.module.css';

const BRAND = '#427898';

const EMPTY_FORM = {
    name: '', description: '', category: '', unitOfMeasure: '',
    sellPrice: '', minStockLevel: '', storageLocation: '',
};
const EMPTY_BATCH = { quantity: '', unitPrice: '', lotNumber: '', receivedDate: '', expiryDate: '' };
const EMPTY_TX = { transactionType: 'OUT', quantity: '', notes: '' };

const TX_LABEL = { IN: 'Stock In', OUT: 'Stock Out', ADJUSTMENT: 'Adjustment' };
const TX_COLOR = { IN: '#16A34A', OUT: '#EF4444', ADJUSTMENT: '#F59E0B' };

function stockMeta(item) {
    if (!item) return { label: 'Unknown', color: '#64748B', bg: '#F1F5F9', strip: '#CBD5E1' };
    if (item.currentStock <= 0)
        return { label: 'Out of stock', color: '#EF4444', bg: '#FEF2F2', strip: '#EF4444' };
    if (item.minStockLevel > 0 && item.currentStock <= item.minStockLevel)
        return { label: 'Low stock', color: '#F59E0B', bg: '#FFFBEB', strip: '#F59E0B' };
    return { label: 'In stock', color: '#16A34A', bg: '#F0FDF4', strip: '#22C55E' };
}

const SideLabel = ({ children }) => (
    <Typography sx={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.6, mb: 0.4 }}>
        {children}
    </Typography>
);

const FieldRow = ({ label, value, last }) => (
    <Box sx={{ py: 1.5, borderBottom: last ? 'none' : '1px solid #F1F5F9' }}>
        <SideLabel>{label}</SideLabel>
        {typeof value === 'string' || value == null
            ? <Typography fontSize={13} fontWeight={500} color="#1E293B">{value || '—'}</Typography>
            : value}
    </Box>
);

const DrawerFieldLabel = ({ children }) => (
    <Typography fontSize={11} fontWeight={600} color="#94A3B8" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>
        {children}
    </Typography>
);

/* ── WorkspaceDrawer ─────────────────────────────────────────────────────── */
const WorkspaceDrawer = ({ open, onClose, title, subtitle, saveLabel, onSave, saving, children }) => (
    <Drawer
        anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 480 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: 3, bgcolor: BRAND, flexShrink: 0 }} />
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography fontWeight={700} fontSize={16} color="#0F172A">{title}</Typography>
                    {subtitle && <Typography fontSize={12} color="#94A3B8" mt={0.3}>{subtitle}</Typography>}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                {children}
            </Box>
            <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving}
                    sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                    {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : saveLabel}
                </Button>
            </Box>
        </Box>
    </Drawer>
);

/* ── Component ───────────────────────────────────────────────────────────── */
const InventoryInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);

    const [batches, setBatches] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);

    const [batchFormOpen, setBatchFormOpen] = useState(false);
    const [batchForm, setBatchForm] = useState(EMPTY_BATCH);

    const [txFormOpen, setTxFormOpen] = useState(false);
    const [txForm, setTxForm] = useState(EMPTY_TX);

    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [editSaving, setEditSaving] = useState(false);

    /* ── load ── */
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const inv = await InventoryService.getById(id);
                if (!inv) { navigate('/inventory'); return; }
                setItem(inv);
            } catch {
                showToast('Failed to load inventory item', 'error');
                navigate('/inventory');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!id) return;
        (async () => {
            setDataLoading(true);
            try {
                const [b, tx] = await Promise.all([
                    InventoryService.getBatchesByInventory(id),
                    InventoryService.getTransactionsByInventory(id),
                ]);
                setBatches(b || []);
                setTransactions(tx || []);
            } catch {
                showToast('Failed to load batches/transactions', 'error');
            } finally {
                setDataLoading(false);
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshItem = async () => {
        const updated = await InventoryService.getById(id);
        setItem(updated);
    };

    /* ── edit ── */
    const openEdit = () => {
        setEditForm({
            name: item.name || '',
            description: item.description || '',
            category: item.category || '',
            unitOfMeasure: item.unitOfMeasure || '',
            sellPrice: item.sellPrice ?? '',
            minStockLevel: item.minStockLevel ?? '',
            storageLocation: item.storageLocation || '',
        });
        setEditOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editForm.name.trim()) { showToast('Name is required', 'error'); return; }
        setEditSaving(true);
        const payload = {
            name: editForm.name.trim(),
            description: editForm.description || null,
            category: editForm.category || null,
            unitOfMeasure: editForm.unitOfMeasure || null,
            sellPrice: parseFloat(editForm.sellPrice) || 0,
            minStockLevel: parseFloat(editForm.minStockLevel) || 0,
            storageLocation: editForm.storageLocation || null,
        };
        try {
            const updated = await InventoryService.update(id, payload);
            setItem(updated);
            setEditOpen(false);
            showToast('Item updated', 'success');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save', 'error');
        } finally {
            setEditSaving(false);
        }
    };

    /* ── batches ── */
    const handleSaveBatch = async () => {
        if (!batchForm.quantity || parseFloat(batchForm.quantity) <= 0) {
            showToast('Quantity must be greater than 0', 'error'); return;
        }
        const payload = {
            inventory: { id: parseInt(id) },
            quantity: parseFloat(batchForm.quantity),
            unitPrice: parseFloat(batchForm.unitPrice) || 0,
            lotNumber: batchForm.lotNumber || null,
            receivedDate: batchForm.receivedDate || null,
            expiryDate: batchForm.expiryDate || null,
        };
        try {
            const created = await InventoryService.createBatch(payload);
            setBatches(prev => [created, ...prev]);
            await refreshItem();
            setBatchForm(EMPTY_BATCH);
            setBatchFormOpen(false);
            showToast('Batch added — stock updated', 'success');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to add batch', 'error');
        }
    };

    const handleDeleteBatch = async (batchId) => {
        try {
            await InventoryService.deleteBatch(batchId);
            setBatches(prev => prev.filter(b => b.id !== batchId));
            await refreshItem();
            showToast('Batch deleted — stock updated', 'warning');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete batch', 'error');
        }
    };

    /* ── transactions ── */
    const handleSaveTx = async () => {
        if (!txForm.quantity || parseFloat(txForm.quantity) <= 0) {
            showToast('Quantity must be greater than 0', 'error'); return;
        }
        const payload = {
            inventory: { id: parseInt(id) },
            transactionType: txForm.transactionType,
            quantity: parseFloat(txForm.quantity),
            notes: txForm.notes || null,
            transactionDate: new Date().toISOString(),
        };
        try {
            const created = await InventoryService.createTransaction(payload);
            setTransactions(prev => [created, ...prev]);
            await refreshItem();
            setTxForm(EMPTY_TX);
            setTxFormOpen(false);
            showToast('Transaction recorded — stock updated', 'success');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to record transaction', 'error');
        }
    };

    const handleDeleteTx = async (txId) => {
        try {
            await InventoryService.deleteTransaction(txId);
            setTransactions(prev => prev.filter(tx => tx.id !== txId));
            await refreshItem();
            showToast('Transaction reversed — stock updated', 'warning');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete transaction', 'error');
        }
    };

    /* ── loading state ── */
    if (loading) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress sx={{ color: BRAND }} />
                </Box>
            </Layout>
        );
    }

    const meta = stockMeta(item);

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, md: 3 } }}>

                {/* ── Header ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton onClick={() => navigate('/inventory')}
                        sx={{ color: '#64748B', border: '1px solid #EEF2F6', borderRadius: 1.5, p: 0.75, '&:hover': { bgcolor: '#F8FAFC' } }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight={700} color="#0F172A" lineHeight={1.2} noWrap>
                            {item?.name}
                        </Typography>
                        {item?.category && (
                            <Typography fontSize={13} color="#64748B" mt={0.2}>{item.category}</Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        <Chip label={meta.label} size="small"
                            sx={{ fontWeight: 700, fontSize: 11, bgcolor: meta.bg, color: meta.color, borderRadius: '6px', height: 24 }} />
                        <Button
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={openEdit}
                            variant="outlined"
                            sx={{ borderColor: '#E2E8F0', color: '#475569', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, '&:hover': { borderColor: BRAND, color: BRAND, bgcolor: '#EEF6FB' } }}
                        >
                            Edit
                        </Button>
                    </Box>
                </Box>

                {/* ── 2-col layout ── */}
                <Box className={styles.infoLayout}>

                    {/* ── Sidebar ── */}
                    <Box className={styles.infoSidebar}>
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden' }}>
                            {/* Stock hero */}
                            <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid #F1F5F9' }}>
                                <Typography fontSize={10} fontWeight={600} color="#94A3B8" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5 }}>
                                    Current Stock
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                    <Typography fontSize={28} fontWeight={800} color={meta.strip} lineHeight={1}>
                                        {item?.currentStock ?? 0}
                                    </Typography>
                                    {item?.unitOfMeasure && (
                                        <Typography fontSize={14} color="#64748B" fontWeight={500}>{item.unitOfMeasure}</Typography>
                                    )}
                                </Box>
                                {item?.minStockLevel > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                                        {item.currentStock <= item.minStockLevel && (
                                            <WarningAmberIcon sx={{ fontSize: 13, color: '#F59E0B' }} />
                                        )}
                                        <Typography fontSize={12} color="#94A3B8">
                                            Min level: {item.minStockLevel} {item.unitOfMeasure || ''}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Details */}
                            <Box sx={{ px: 2.5, py: 1 }}>
                                <FieldRow label="Description" value={item?.description} />
                                <FieldRow label="Unit of measure" value={item?.unitOfMeasure} />
                                <FieldRow label="Sell price" value={item?.sellPrice > 0 ? `CHF ${item.sellPrice.toFixed(2)}` : '—'} />
                                <FieldRow label="Storage location" value={item?.storageLocation} last />
                            </Box>
                        </Paper>
                    </Box>

                    {/* ── Main content ── */}
                    <Box className={styles.infoMain}>
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden' }}>
                            <Tabs
                                value={tab}
                                onChange={(_, v) => { setTab(v); setBatchFormOpen(false); setTxFormOpen(false); }}
                                sx={{
                                    px: 2, borderBottom: '1px solid #EEF2F6',
                                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44 },
                                    '& .Mui-selected': { color: BRAND },
                                    '& .MuiTabs-indicator': { bgcolor: BRAND },
                                }}
                            >
                                <Tab label={`Batches (${batches.length})`} />
                                <Tab label={`Movements (${transactions.length})`} />
                            </Tabs>

                            <Box sx={{ p: 3 }}>
                                {dataLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={24} sx={{ color: BRAND }} />
                                    </Box>
                                ) : (
                                    <>
                                        {/* ── Batches tab ── */}
                                        {tab === 0 && (
                                            <Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                    <Typography fontSize={13} fontWeight={600} color="#0F172A">Stock Receipts</Typography>
                                                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                                                        onClick={() => setBatchFormOpen(v => !v)}
                                                        sx={{ textTransform: 'none', fontSize: 12, color: BRAND, fontWeight: 600, borderRadius: 1.5, '&:hover': { bgcolor: '#EEF6FB' } }}>
                                                        Add Batch
                                                    </Button>
                                                </Box>

                                                <Collapse in={batchFormOpen}>
                                                    <Box sx={{ border: '1px solid #EEF2F6', borderRadius: 2, p: 2.5, mb: 2.5, bgcolor: '#FAFBFC' }}>
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                                            <TextField label="Quantity *" type="number" size="small"
                                                                value={batchForm.quantity}
                                                                onChange={e => setBatchForm(p => ({ ...p, quantity: e.target.value }))}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                            <TextField label="Unit price (CHF)" type="number" size="small"
                                                                value={batchForm.unitPrice}
                                                                onChange={e => setBatchForm(p => ({ ...p, unitPrice: e.target.value }))}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                            <TextField label="Lot / reference" size="small"
                                                                value={batchForm.lotNumber}
                                                                onChange={e => setBatchForm(p => ({ ...p, lotNumber: e.target.value }))}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                            <TextField label="Received date" type="date" size="small"
                                                                InputLabelProps={{ shrink: true }}
                                                                value={batchForm.receivedDate}
                                                                onChange={e => setBatchForm(p => ({ ...p, receivedDate: e.target.value }))}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                            <TextField label="Expiry date" type="date" size="small"
                                                                InputLabelProps={{ shrink: true }}
                                                                value={batchForm.expiryDate}
                                                                onChange={e => setBatchForm(p => ({ ...p, expiryDate: e.target.value }))}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                            <Button size="small" onClick={() => setBatchFormOpen(false)}
                                                                sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
                                                            <Button variant="contained" size="small" onClick={handleSaveBatch}
                                                                sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none' }}>
                                                                Add to stock
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Collapse>

                                                {batches.length === 0 ? (
                                                    <Box sx={{ py: 4, textAlign: 'center', border: '1.5px dashed #E2E8F0', borderRadius: 2 }}>
                                                        <Typography fontSize={13} color="#94A3B8">No batches yet. Add a batch to record incoming stock.</Typography>
                                                    </Box>
                                                ) : (
                                                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 1.5 }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                                                                    {['Qty', 'Unit price', 'Lot', 'Received', 'Expires', ''].map(h => (
                                                                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #EEF2F6' }}>{h}</TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {batches.map(b => {
                                                                    const expiringSoon = b.expiryDate && new Date(b.expiryDate) < new Date(Date.now() + 30 * 86400000);
                                                                    return (
                                                                        <TableRow key={b.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                                            <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{b.quantity} {item?.unitOfMeasure || ''}</TableCell>
                                                                            <TableCell sx={{ fontSize: 13 }}>{b.unitPrice > 0 ? `CHF ${b.unitPrice.toFixed(2)}` : '—'}</TableCell>
                                                                            <TableCell sx={{ fontSize: 13, color: '#64748B' }}>{b.lotNumber || '—'}</TableCell>
                                                                            <TableCell sx={{ fontSize: 13, color: '#64748B' }}>{b.receivedDate ? new Date(b.receivedDate).toLocaleDateString('de-CH') : '—'}</TableCell>
                                                                            <TableCell>
                                                                                {b.expiryDate ? (
                                                                                    <Tooltip title={expiringSoon ? 'Expires within 30 days' : ''} arrow>
                                                                                        <Typography fontSize={13} sx={{ color: expiringSoon ? '#EF4444' : '#64748B', fontWeight: expiringSoon ? 700 : 400 }}>
                                                                                            {new Date(b.expiryDate).toLocaleDateString('de-CH')}
                                                                                        </Typography>
                                                                                    </Tooltip>
                                                                                ) : '—'}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Tooltip title="Delete batch" arrow>
                                                                                    <IconButton size="small" onClick={() => handleDeleteBatch(b.id)}
                                                                                        sx={{ color: '#CBD5E1', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                )}
                                            </Box>
                                        )}

                                        {/* ── Transactions tab ── */}
                                        {tab === 1 && (
                                            <Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                    <Typography fontSize={13} fontWeight={600} color="#0F172A">Movement Log</Typography>
                                                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                                                        onClick={() => setTxFormOpen(v => !v)}
                                                        sx={{ textTransform: 'none', fontSize: 12, color: BRAND, fontWeight: 600, borderRadius: 1.5, '&:hover': { bgcolor: '#EEF6FB' } }}>
                                                        Record Movement
                                                    </Button>
                                                </Box>

                                                <Collapse in={txFormOpen}>
                                                    <Box sx={{ border: '1px solid #EEF2F6', borderRadius: 2, p: 2.5, mb: 2.5, bgcolor: '#FAFBFC' }}>
                                                        {txForm.transactionType === 'OUT' && txForm.quantity && parseFloat(txForm.quantity) > (item?.currentStock ?? 0) && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                                                                <WarningAmberIcon sx={{ color: '#F59E0B', fontSize: 16 }} />
                                                                <Typography fontSize={12} color="#92400E">
                                                                    Only {item?.currentStock ?? 0} {item?.unitOfMeasure || ''} in stock — this will be rejected.
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                                            <FormControl size="small">
                                                                <InputLabel>Type *</InputLabel>
                                                                <Select value={txForm.transactionType} label="Type *"
                                                                    onChange={e => setTxForm(p => ({ ...p, transactionType: e.target.value }))}
                                                                    sx={{ borderRadius: 1.5 }}>
                                                                    <MenuItem value="OUT" sx={{ fontSize: 13 }}>Stock Out — use / assign</MenuItem>
                                                                    <MenuItem value="IN" sx={{ fontSize: 13 }}>Stock In — manual add</MenuItem>
                                                                    <MenuItem value="ADJUSTMENT" sx={{ fontSize: 13 }}>Adjustment — correction</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            <TextField label="Quantity *" type="number" size="small"
                                                                value={txForm.quantity}
                                                                onChange={e => setTxForm(p => ({ ...p, quantity: e.target.value }))}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                        </Box>
                                                        <TextField label="Notes" size="small" fullWidth multiline rows={1}
                                                            value={txForm.notes}
                                                            onChange={e => setTxForm(p => ({ ...p, notes: e.target.value }))}
                                                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                            <Button size="small" onClick={() => setTxFormOpen(false)}
                                                                sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
                                                            <Button variant="contained" size="small" onClick={handleSaveTx}
                                                                sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none' }}>
                                                                Record
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Collapse>

                                                {transactions.length === 0 ? (
                                                    <Box sx={{ py: 4, textAlign: 'center', border: '1.5px dashed #E2E8F0', borderRadius: 2 }}>
                                                        <Typography fontSize={13} color="#94A3B8">No movements recorded yet.</Typography>
                                                    </Box>
                                                ) : (
                                                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 1.5 }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                                                                    {['Type', 'Qty', 'Date', 'Notes', ''].map(h => (
                                                                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #EEF2F6' }}>{h}</TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {transactions.map(tx => (
                                                                    <TableRow key={tx.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                                        <TableCell>
                                                                            <Chip
                                                                                label={TX_LABEL[tx.transactionType] || tx.transactionType}
                                                                                size="small"
                                                                                sx={{ bgcolor: `${TX_COLOR[tx.transactionType]}18`, color: TX_COLOR[tx.transactionType], fontWeight: 700, fontSize: 11, borderRadius: '6px', height: 20 }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                                                                            <Typography component="span" sx={{ color: tx.transactionType === 'OUT' ? '#EF4444' : '#16A34A', fontWeight: 700 }}>
                                                                                {tx.transactionType === 'OUT' ? '−' : '+'}
                                                                            </Typography>
                                                                            {tx.quantity} {item?.unitOfMeasure || ''}
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: 13, color: '#64748B' }}>
                                                                            {tx.transactionDate ? new Date(tx.transactionDate).toLocaleDateString('de-CH') : '—'}
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: 13, color: '#64748B' }}>{tx.notes || '—'}</TableCell>
                                                                        <TableCell>
                                                                            <Tooltip title="Delete and reverse stock" arrow>
                                                                                <IconButton size="small" onClick={() => handleDeleteTx(tx.id)}
                                                                                    sx={{ color: '#CBD5E1', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                )}
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Box>

            {/* ── Edit item drawer ── */}
            <WorkspaceDrawer
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title="Edit Item"
                subtitle={item?.name}
                saveLabel="Save Changes"
                onSave={handleSaveEdit}
                saving={editSaving}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                        <DrawerFieldLabel>Name *</DrawerFieldLabel>
                        <TextField fullWidth size="small" value={editForm.name}
                            onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                    </Box>
                    <Box>
                        <DrawerFieldLabel>Description</DrawerFieldLabel>
                        <TextField fullWidth size="small" multiline rows={2} value={editForm.description}
                            onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <DrawerFieldLabel>Category</DrawerFieldLabel>
                            <TextField fullWidth size="small" value={editForm.category}
                                onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                        <Box>
                            <DrawerFieldLabel>Unit of measure</DrawerFieldLabel>
                            <TextField fullWidth size="small" placeholder="kg, m², Stk" value={editForm.unitOfMeasure}
                                onChange={e => setEditForm(p => ({ ...p, unitOfMeasure: e.target.value }))}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <DrawerFieldLabel>Sell price (CHF)</DrawerFieldLabel>
                            <TextField fullWidth size="small" type="number" value={editForm.sellPrice}
                                onChange={e => setEditForm(p => ({ ...p, sellPrice: e.target.value }))}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                        <Box>
                            <DrawerFieldLabel>Min. stock level</DrawerFieldLabel>
                            <TextField fullWidth size="small" type="number" value={editForm.minStockLevel}
                                onChange={e => setEditForm(p => ({ ...p, minStockLevel: e.target.value }))}
                                inputProps={{ min: 0 }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                    </Box>
                    <Box>
                        <DrawerFieldLabel>Storage location</DrawerFieldLabel>
                        <TextField fullWidth size="small" placeholder="e.g. Shelf A-12" value={editForm.storageLocation}
                            onChange={e => setEditForm(p => ({ ...p, storageLocation: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                    </Box>
                </Box>
            </WorkspaceDrawer>
        </Layout>
    );
};

export default InventoryInfo;
